<template>
  <div
    class="scale-question"
    :class="[
      `scale-question--${layout}`,
      `scale-question--${resolvedOptionType}`,
      { 'scale-question--scrollable': isScrollable, 'scale-question--gradient': useGradientStyle }
    ]"
    :style="scaleThemeStyle"
    role="radiogroup"
    :aria-label="ariaLabel"
  >
    <template v-if="useGradientStyle">
      <div class="scale-question__nps-block">
        <div
          v-if="prefixText || suffixText"
          class="scale-question__legend"
        >
          <span
            v-if="prefixText"
            class="scale-question__legend-label scale-question__legend-label--start"
          >{{ prefixText }}</span>
          <span
            v-if="suffixText"
            class="scale-question__legend-label scale-question__legend-label--end"
          >{{ suffixText }}</span>
        </div>
        <div
          class="scale-question__track scale-question__track--nps"
          :style="trackGapStyle"
        >
          <button
            v-for="(option, index) of displayOptions"
            :key="getOptionKey(option, index)"
            type="button"
            class="scale-question__item--nps"
            :class="{ 'is-selected': isSelected(option) }"
            role="radio"
            :aria-checked="isSelected(option)"
            :disabled="disabled"
            @click="onSelect(option)"
          >
            <span class="scale-question__label">{{ option.label }}</span>
          </button>
        </div>
      </div>
    </template>
    <template v-else>
      <div
        v-if="layout === 'mobile'"
        class="scale-question__anchors scale-question__anchors--mobile"
      >
        <span v-if="prefixText" class="scale-question__anchor">{{ prefixText }}</span>
        <span v-if="suffixText" class="scale-question__anchor scale-question__anchor--end">{{ suffixText }}</span>
      </div>
      <div class="scale-question__row">
        <span
          v-if="prefixText && layout !== 'mobile'"
          class="scale-question__anchor"
        >{{ prefixText }}</span>
        <div class="scale-question__track">
          <button
            v-for="(option, index) of displayOptions"
            :key="getOptionKey(option, index)"
            type="button"
            class="scale-question__item"
            :class="{ 'is-selected': isSelected(option) }"
            role="radio"
            :aria-checked="isSelected(option)"
            :disabled="disabled"
            @click="onSelect(option)"
          >
            <span
              v-if="resolvedOptionType === 'round'"
              class="scale-question__dot"
            />
            <span v-else class="scale-question__label">{{ option.label }}</span>
          </button>
        </div>
        <span
          v-if="suffixText && layout !== 'mobile'"
          class="scale-question__anchor scale-question__anchor--end"
        >{{ suffixText }}</span>
      </div>
    </template>
  </div>
</template>

<script>
const DEFAULT_SCALE_THEME_COLOR = '#4c4edb'

export default {
  name: 'ScaleQuestion',
  props: {
    value: {
      type: [String, Number],
      default: undefined
    },
    options: {
      type: Array,
      default() {
        return []
      }
    },
    prefixText: {
      type: String,
      default: ''
    },
    suffixText: {
      type: String,
      default: ''
    },
    optionType: {
      type: String,
      default: 'default'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    layout: {
      type: String,
      default: 'pc',
      validator(val) {
        return ['pc', 'mobile'].indexOf(val) > -1
      }
    },
    themeColor: {
      type: String,
      default: ''
    },
    ariaLabel: {
      type: String,
      default: '量表题选项'
    }
  },
  computed: {
    resolvedOptionType() {
      const type = this.optionType || 'default'
      if (['default', 'round', 'button'].indexOf(type) > -1) {
        return type
      }
      return 'default'
    },
    displayOptions() {
      return Array.isArray(this.options) ? this.options : []
    },
    isScrollable() {
      // 渐变量表优先等比缩小适配窄容器（如钉钉桌面侧边栏），不启用横向滚动
      if (this.useGradientStyle) {
        return false
      }
      return this.layout === 'mobile' && this.displayOptions.length >= 7
    },
    pcGradientItemWidth() {
      if (this.layout !== 'pc' || !this.useGradientStyle) {
        return 44
      }
      const count = this.displayOptions.length
      if (count <= 6) {
        return 48
      }
      if (count <= 8) {
        return 44
      }
      if (count <= 10) {
        return 40
      }
      return 36
    },
    trackGapStyle() {
      return {
        gap: this.npsGap,
        '--pc-nps-item-width': `${this.pcGradientItemWidth}px`,
        '--nps-item-font-size': this.npsItemFontSize
      }
    },
    npsGap() {
      if (this.layout === 'mobile' && this.useGradientStyle) {
        return '2px'
      }
      if (this.layout === 'pc' && this.useGradientStyle) {
        // 钉钉侧边栏等窄宽场景：选项多时减小间距，便于整行放下
        if (this.displayOptions.length >= 10) {
          return '3px'
        }
        if (this.displayOptions.length >= 8) {
          return '5px'
        }
        return '8px'
      }
      return '6px'
    },
    npsItemFontSize() {
      if (!this.useGradientStyle) {
        return '14px'
      }
      if (this.layout === 'mobile') {
        return '12px'
      }
      if (this.displayOptions.length >= 10) {
        return '12px'
      }
      if (this.displayOptions.length >= 8) {
        return '13px'
      }
      return '14px'
    },
    useGradientStyle() {
      return this.resolvedOptionType === 'default'
    },
    resolvedThemeColor() {
      return this.themeColor || DEFAULT_SCALE_THEME_COLOR
    },
    scaleThemeStyle() {
      return {
        '--form-theme-color': this.resolvedThemeColor,
        '--scale-theme': this.resolvedThemeColor
      }
    }
  },
  methods: {
    getOptionKey(option, index) {
      if (option.uuid !== undefined && option.uuid !== null && option.uuid !== '') {
        return `uuid_${option.uuid}`
      }
      if (option.label !== undefined && option.label !== null) {
        return `value_${option.label}`
      }
      return `idx_${index}`
    },
    isSelected(option) {
      if (this.value === undefined || this.value === null || this.value === '') {
        return false
      }
      return String(this.value) === String(option.label)
    },
    onSelect(option) {
      if (this.disabled) {
        return
      }
      const nextValue = option.label
      this.$emit('input', nextValue)
      this.$emit('change', nextValue)
    }
  }
}
</script>

<style lang="scss" scoped>
.scale-question {
  --scale-text: var(--question-text-color, #303133);
  --scale-theme: var(--form-theme-color, #4c4edb);
  width: 100%;
  padding-left: var(--question-indent-base, 3%);
  padding-right: var(--question-indent-base, 3%);
  color: var(--scale-text);
}

.scale-question--gradient {
  padding: 2px 0;
}

/* 文案与选项同一容器，保证两端对齐 */
.scale-question__nps-block {
  width: 100%;
  min-width: 0;
}

.scale-question--pc.scale-question--gradient .scale-question__nps-block {
  /* 窄容器（钉钉侧边栏）下随父级收缩，避免 fit-content 撑破后被 overflow 裁切 */
  width: 100%;
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
}

.scale-question__legend {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 22px;
  margin-bottom: 8px;
  box-sizing: border-box;
}

.scale-question__legend-label {
  flex: 0 1 auto;
  max-width: 48%;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
  color: #909399;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.scale-question__legend-label--start {
  text-align: left;
}

.scale-question__legend-label--end {
  margin-left: auto;
  text-align: right;
}

.scale-question__track--nps {
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  gap: 8px;
  width: 100%;
  min-width: 0;
}

.scale-question--pc.scale-question--gradient .scale-question__track--nps {
  width: 100%;
  min-width: 0;
  justify-content: center;
}

.scale-question--scrollable .scale-question__track--nps {
  overflow-x: auto;
  justify-content: flex-start;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 4px;
}

.scale-question__item--nps {
  flex: 1 1 0;
  min-width: 0;
  max-width: 52px;
  height: 40px;
  margin: 0;
  padding: 0 2px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  background: #fff;
  color: #606266;
  font-size: var(--nps-item-font-size, 14px);
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  outline: none;
  box-sizing: border-box;
  overflow: hidden;
}

.scale-question--pc.scale-question--gradient .scale-question__item--nps {
  /* 宽屏保持偏好宽度，窄屏等比缩小，保证 1~10 分都能落在可视区域内 */
  flex: 1 1 0;
  min-width: 0;
  width: auto;
  max-width: var(--pc-nps-item-width, 44px);
  height: 40px;
}

.scale-question--scrollable .scale-question__item--nps {
  flex: 0 0 40px;
  min-width: 40px;
}

.scale-question__item--nps.is-selected {
  color: #fff;
  background: var(--scale-theme);
  border-color: var(--scale-theme);
}

.scale-question__item--nps:hover:not(:disabled):not(.is-selected) {
  border-color: var(--scale-theme);
  color: var(--scale-theme);
}

.scale-question__item--nps:focus-visible {
  box-shadow: 0 0 0 2px var(--form-theme-color-shadow, rgba(76, 78, 219, 0.2));
}

.scale-question__item--nps:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.scale-question--mobile.scale-question--gradient {
  margin-left: 0;
  margin-right: 0;
  width: 100%;
}

.scale-question--mobile.scale-question--gradient .scale-question__legend {
  margin-bottom: 6px;
}

.scale-question--mobile.scale-question--gradient .scale-question__legend-label {
  font-size: 12px;
}

.scale-question--mobile.scale-question--gradient .scale-question__track--nps {
  gap: 2px;
  overflow: visible;
  justify-content: stretch;
}

.scale-question--mobile.scale-question--gradient .scale-question__item--nps {
  flex: 1 1 0;
  min-width: 0;
  max-width: none;
  width: auto;
  height: 34px;
  font-size: var(--nps-item-font-size, 12px);
  border-radius: 6px;
}

/* 圆点 / 胶囊样式（非 default） */
.scale-question__anchors--mobile {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding: 0 2px;
  gap: 12px;
}

.scale-question__row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.scale-question--pc .scale-question__row {
  justify-content: center;
}

.scale-question--mobile .scale-question__row {
  flex-direction: column;
  align-items: stretch;
  gap: 0;
}

.scale-question__anchor {
  flex-shrink: 0;
  max-width: 28%;
  font-size: 13px;
  line-height: 1.4;
  color: #64748b;
  text-align: left;
}

.scale-question__anchor--end {
  text-align: right;
}

.scale-question__track {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 0;
}

.scale-question--pc .scale-question__track {
  flex: 0 1 auto;
}

.scale-question--mobile .scale-question__track {
  width: 100%;
  padding: 4px 0;
}

.scale-question--scrollable .scale-question__track {
  overflow-x: auto;
  justify-content: flex-start;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 4px;
}

.scale-question__item {
  flex: 1;
  min-width: 0;
  max-width: 56px;
  height: 44px;
  margin: 0;
  padding: 0;
  border: 1px solid #dcdfe6;
  border-radius: 50%;
  background: #fff;
  color: var(--scale-text);
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  outline: none;
}

.scale-question--pc.scale-question--default .scale-question__item {
  flex: 0 0 44px;
}

.scale-question--pc.scale-question--button .scale-question__item {
  flex: 0 0 auto;
}

.scale-question__item.is-selected {
  border-color: var(--scale-theme);
  background: var(--scale-theme);
  color: #fff;
}

.scale-question__item:hover:not(:disabled):not(.is-selected) {
  border-color: var(--scale-theme);
  color: var(--scale-theme);
}

.scale-question__item:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.scale-question--button .scale-question__item {
  border-radius: 999px;
  max-width: none;
  min-width: 40px;
  height: 36px;
  padding: 0 10px;
}

.scale-question--round .scale-question__item {
  width: 36px;
  height: 36px;
  min-width: 36px;
  max-width: 36px;
  flex: 0 0 36px;
  padding: 0;
}

.scale-question__dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.35;
}

.scale-question--round .scale-question__item.is-selected .scale-question__dot {
  opacity: 1;
}

.scale-question__label {
  pointer-events: none;
  user-select: none;
  white-space: nowrap;
  overflow: hidden;
  max-width: 100%;
}
</style>
